/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	MenuItem,
	Switch,
	Button,
} from '@mui/material'
import {
	DealerType,
	DealerPriceSheetRowType,
	DealerPriceSaveType,
	DealerPriceRuleType,
	DealerCurrency,
} from '@schemas'
import {
	mono,
	boFont,
	boShared,
	boMedia,
	boTableAsCards,
} from '@components/systems/backoffice/backoffice.tokens'

interface PropsType {
	dealer: DealerType
	rows: DealerPriceSheetRowType[]
	isEditMode: boolean
	isSaving: boolean
	/** 저장이 끝났을 때 올라간다 — 로컬 편집 상태를 서버 값으로 되돌리는 신호 */
	resetKey: number
	onSave: (body: DealerPriceSaveType) => void
}

type RuleSelect = 'inherit' | DealerPriceRuleType

type RuleForm = {
	ruleType: RuleSelect
	/** rate 면 퍼센트(75), minus·fixed 면 업체 통화 금액 */
	value: string
	useYn: number
}

type ProfileForm = {
	companyName: string
	currency: DealerCurrency
	ruleType: RuleSelect
	value: string
	vatExtraYn: number
	shippingExtraYn: number
	dutyExtraYn: number
}

const RULE_OPTIONS: { value: RuleSelect; label: string }[] = [
	{ value: 'inherit', label: '기본 상속' },
	{ value: 'rate', label: '정가 × %' },
	{ value: 'minus', label: '정가 − 금액' },
	{ value: 'fixed', label: '고정 금액' },
]

const PROFILE_RULE_OPTIONS: { value: RuleSelect; label: string }[] = [
	{ value: 'inherit', label: '정가 그대로' },
	{ value: 'rate', label: '정가 × %' },
	{ value: 'minus', label: '정가 − 금액' },
	{ value: 'fixed', label: '고정 금액' },
]

const toNumber = (v: string): number | null => {
	if (v.trim() === '') return null
	const n = Number(v.replace(/,/g, ''))
	return Number.isNaN(n) ? null : n
}

const money = (currency: DealerCurrency, value: number | null): string => {
	if (value === null) return '—'
	return currency === 'KRW'
		? `₩${Math.round(value).toLocaleString('ko-KR')}`
		: `$${value.toLocaleString('en-US', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
		  })}`
}

const percent = (value: number | null): string =>
	value === null ? '—' : `${(value * 100).toFixed(1)}%`

/**
 * 납품가 계산 — 서버 `DealerPricing.compute` 와 같은 식(미리보기용). 정본은 서버다.
 * 반올림 없음(사용자 결정). USD 는 센트 아래 찌꺼기만 지운다.
 */
const compute = (
	rule: { ruleType: RuleSelect; value: string },
	fallback: { ruleType: RuleSelect; value: string },
	basePrice: number,
	currency: DealerCurrency,
): number | null => {
	const effective = rule.ruleType === 'inherit' ? fallback : rule
	if (effective.ruleType === 'inherit') return basePrice
	const n = toNumber(effective.value)
	if (n === null) return null
	let value: number
	if (effective.ruleType === 'rate') value = basePrice * (n / 100)
	else if (effective.ruleType === 'minus') value = basePrice - n
	else value = n
	return currency === 'USD' ? Number(value.toFixed(2)) : value
}

const ruleToForm = (
	ruleType: DealerPriceRuleType | null,
	rate: number | null,
	amountKrw: number | null,
	amountUsd: number | null,
	currency: DealerCurrency,
): { ruleType: RuleSelect; value: string } => {
	if (ruleType === null) return { ruleType: 'inherit', value: '' }
	if (ruleType === 'rate') {
		return {
			ruleType,
			value: rate === null ? '' : String(Math.round(rate * 10000) / 100),
		}
	}
	const amount = currency === 'KRW' ? amountKrw : amountUsd
	return { ruleType, value: amount === null ? '' : String(amount) }
}

const formToRule = (
	form: { ruleType: RuleSelect; value: string },
	currency: DealerCurrency,
): {
	ruleType: DealerPriceRuleType | null
	rate: number | null
	amountKrw: number | null
	amountUsd: number | null
} => {
	if (form.ruleType === 'inherit') {
		return { ruleType: null, rate: null, amountKrw: null, amountUsd: null }
	}
	const n = toNumber(form.value)
	if (form.ruleType === 'rate') {
		return {
			ruleType: 'rate',
			rate: n === null ? null : n / 100,
			amountKrw: null,
			amountUsd: null,
		}
	}
	return {
		ruleType: form.ruleType,
		rate: null,
		amountKrw: currency === 'KRW' ? n : null,
		amountUsd: currency === 'USD' ? n : null,
	}
}

/**
 * 납품가 관리 우측 — 업체 정보(통화 · 기본 규칙 · 옵션 셋) + 제품별 규칙 표 (DISPATCH_PLAN §4.3·§4.6).
 * 값을 넣으면 납품가와 마진이 바로 계산된다. 저장은 표 전체 한 번.
 */
export const DealerPriceSheet = (props: PropsType): JSX.Element => {
	const { dealer, rows, isEditMode, isSaving } = props

	const [profile, setProfile] = React.useState<ProfileForm>(() => initProfile(dealer))
	const [ruleForms, setRuleForms] = React.useState<{ [productId: string]: RuleForm }>(
		() => initRules(rows, dealer.currency),
	)

	React.useEffect(() => {
		setProfile(initProfile(dealer))
		setRuleForms(initRules(rows, dealer.currency))
	}, [dealer, rows, props.resetKey])

	// 통화를 바꾸면 금액 규칙의 값은 의미가 달라진다 — rate 는 그대로, 금액은 비운다
	const changeCurrency = (currency: DealerCurrency) => {
		setProfile(prev => ({
			...prev,
			currency,
			value: prev.ruleType === 'rate' ? prev.value : '',
		}))
		setRuleForms(prev =>
			Object.fromEntries(
				Object.entries(prev).map(([id, f]) => [
					id,
					{ ...f, value: f.ruleType === 'rate' ? f.value : '' },
				]),
			),
		)
	}

	const editRule = (productId: string, patch: Partial<RuleForm>) => {
		setRuleForms(prev => ({ ...prev, [productId]: { ...prev[productId], ...patch } }))
	}

	const currency = profile.currency
	const fallback = { ruleType: profile.ruleType, value: profile.value }

	const onSave = () => {
		const body: DealerPriceSaveType = {
			profile: {
				companyName: profile.companyName.trim(),
				currency,
				vatExtraYn: profile.vatExtraYn,
				shippingExtraYn: profile.shippingExtraYn,
				dutyExtraYn: profile.dutyExtraYn,
				...formToRule({ ruleType: profile.ruleType, value: profile.value }, currency),
			},
			rows: rows.map(aRow => {
				const f = ruleForms[aRow.productId] ?? {
					ruleType: 'inherit',
					value: '',
					useYn: 1,
				}
				return {
					productId: aRow.productId,
					useYn: f.useYn,
					...formToRule(f, currency),
				}
			}),
		}
		props.onSave(body)
	}

	const valueLabel = (ruleType: RuleSelect): string =>
		ruleType === 'rate' ? '%' : currency === 'KRW' ? '₩' : '$'

	return (
		<View css={style}>
			{/* ── 업체 정보 ─────────────────────────────────────────── */}
			<View className="profileBand">
				<View className="profileGrid">
					<TextField
						label="업체명"
						value={profile.companyName}
						onChange={e => setProfile(p => ({ ...p, companyName: e.target.value }))}
						size="small"
						disabled={!isEditMode}
						helperText={isEditMode ? '' : dealer.email}
					/>
					<TextField
						select
						label="통화"
						value={profile.currency}
						onChange={e => changeCurrency(e.target.value as DealerCurrency)}
						size="small"
						disabled={!isEditMode}
					>
						<MenuItem value="USD">USD</MenuItem>
						<MenuItem value="KRW">KRW</MenuItem>
					</TextField>
					<TextField
						select
						label="기본 규칙"
						value={profile.ruleType}
						onChange={e =>
							setProfile(p => ({
								...p,
								ruleType: e.target.value as RuleSelect,
								value: '',
							}))
						}
						size="small"
						disabled={!isEditMode}
						helperText="제품별 예외가 없는 제품에 적용"
					>
						{PROFILE_RULE_OPTIONS.map(o => (
							<MenuItem key={o.value} value={o.value}>
								{o.label}
							</MenuItem>
						))}
					</TextField>
					<TextField
						label={`기본 규칙 값 (${valueLabel(profile.ruleType)})`}
						value={profile.value}
						onChange={e => setProfile(p => ({ ...p, value: e.target.value }))}
						size="small"
						disabled={!isEditMode || profile.ruleType === 'inherit'}
						inputProps={{ inputMode: 'decimal' }}
					/>
				</View>

				<View className="optionRow">
					{(
						[
							['vatExtraYn', '부가세 별도', '공급가에 10% 를 얹어 따로 받는다'],
							['shippingExtraYn', '배송비 별도', '배송비를 주문에 기입해 따로 받는다'],
							['dutyExtraYn', '관세 별도', '관세를 주문에 기입해 따로 받는다'],
						] as [keyof ProfileForm, string, string][]
					).map(([key, label, hint]) => (
						<label key={key} className="optionItem">
							<Switch
								size="small"
								checked={profile[key] === 1}
								disabled={!isEditMode}
								onChange={e =>
									setProfile(p => ({ ...p, [key]: e.target.checked ? 1 : 0 }))
								}
							/>
							<span className="optionLabel">{label}</span>
							<span className="optionHint">{hint}</span>
						</label>
					))}
				</View>
			</View>

			{/* ── 제품별 규칙 표 ────────────────────────────────────── */}
			<TableContainer className="sheetTableContainer">
				<Table stickyHeader size="small">
					<TableHead>
						<TableRow>
							<TableCell className="headerCell">제품</TableCell>
							<TableCell className="headerCell right">정가 ({currency})</TableCell>
							<TableCell className="headerCell">규칙</TableCell>
							<TableCell className="headerCell right">값</TableCell>
							<TableCell className="headerCell right">납품가</TableCell>
							<TableCell className="headerCell right">딜러 마진</TableCell>
							<TableCell className="headerCell right">우리 마진</TableCell>
							<TableCell className="headerCell center">취급</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map(aRow => {
							const f = ruleForms[aRow.productId] ?? {
								ruleType: 'inherit' as RuleSelect,
								value: '',
								useYn: 1,
							}
							const basePrice = currency === 'KRW' ? aRow.listKrw : aRow.listUsd
							const cost =
								currency === 'KRW' ? aRow.materialCostKrw : aRow.materialCostUsd
							const supply =
								f.useYn === 0 ? null : compute(f, fallback, basePrice, currency)
							const dealerMargin =
								supply === null || basePrice === 0
									? null
									: (basePrice - supply) / basePrice
							const ourMargin =
								supply === null || supply === 0 || cost === null
									? null
									: (supply - cost) / supply
							const isInherit = f.ruleType === 'inherit'

							return (
								<TableRow
									key={aRow.productId}
									className={`sheetRow ${f.useYn === 0 ? 'off' : ''}`}
								>
									<TableCell className="cardTitle">
										<span className="productName">{aRow.name}</span>
										<span className="productCode">{aRow.code}</span>
									</TableCell>
									<TableCell data-label="정가" className="right num">
										{basePrice === 0 ? (
											<span className="warn">정가 없음</span>
										) : (
											money(currency, basePrice)
										)}
									</TableCell>
									<TableCell data-label="규칙">
										{isEditMode ? (
											<TextField
												select
												value={f.ruleType}
												onChange={e =>
													editRule(aRow.productId, {
														ruleType: e.target.value as RuleSelect,
														value: '',
													})
												}
												size="small"
												fullWidth
											>
												{RULE_OPTIONS.map(o => (
													<MenuItem key={o.value} value={o.value}>
														{o.label}
													</MenuItem>
												))}
											</TextField>
										) : (
											<span className={isInherit ? 'inherit' : ''}>
												{isInherit
													? `기본 (${labelOf(fallback, currency)})`
													: labelOf(f, currency)}
											</span>
										)}
									</TableCell>
									<TableCell data-label="값" className="right num">
										{isEditMode ? (
											<TextField
												value={f.value}
												onChange={e =>
													editRule(aRow.productId, { value: e.target.value })
												}
												size="small"
												disabled={isInherit}
												placeholder={isInherit ? '' : valueLabel(f.ruleType)}
												inputProps={{
													inputMode: 'decimal',
													style: { textAlign: 'right' },
												}}
											/>
										) : isInherit ? (
											<span className="inherit">—</span>
										) : (
											`${f.value} ${valueLabel(f.ruleType)}`
										)}
									</TableCell>
									<TableCell data-label="납품가" className="right num supply">
										{f.useYn === 0 ? (
											<span className="inherit">미취급</span>
										) : (
											money(currency, supply)
										)}
									</TableCell>
									<TableCell
										data-label="딜러 마진"
										className={`right num ${
											dealerMargin !== null && dealerMargin < 0 ? 'error' : ''
										}`}
									>
										{percent(dealerMargin)}
									</TableCell>
									<TableCell
										data-label="우리 마진"
										className={`right num ${
											ourMargin !== null && ourMargin < 0 ? 'error' : ''
										}`}
									>
										{cost === null ? (
											<span className="inherit">원가 없음</span>
										) : (
											percent(ourMargin)
										)}
									</TableCell>
									<TableCell data-label="취급" className="center">
										<Switch
											size="small"
											checked={f.useYn !== 0}
											disabled={!isEditMode}
											onChange={e =>
												editRule(aRow.productId, { useYn: e.target.checked ? 1 : 0 })
											}
										/>
									</TableCell>
								</TableRow>
							)
						})}
					</TableBody>
				</Table>
			</TableContainer>

			<p className="sheetNote">
				기준가 = 정가(상품 관리 기준단가). 시즌·프로모션 값과 무관하다. 반올림 없음. 딜러
				마진 = (정가 − 납품가) ÷ 정가, 우리 마진 = (납품가 − 재료원가) ÷ 납품가.
			</p>

			{isEditMode && (
				<View className="actionRow">
					<Button
						variant="contained"
						className="boBtn"
						disabled={isSaving}
						onClick={onSave}
					>
						{isSaving ? '저장 중' : '저장'}
					</Button>
				</View>
			)}
		</View>
	)
}

const labelOf = (
	form: { ruleType: RuleSelect; value: string },
	currency: DealerCurrency,
) => {
	if (form.ruleType === 'inherit') return '정가'
	if (form.ruleType === 'rate') return `${form.value || '?'}%`
	const n = toNumber(form.value)
	const text =
		n === null
			? '?'
			: currency === 'KRW'
			? Math.round(n).toLocaleString('ko-KR')
			: n.toFixed(2)
	return form.ruleType === 'minus' ? `−${text}` : `= ${text}`
}

const initProfile = (dealer: DealerType): ProfileForm => {
	const r = ruleToForm(
		dealer.defaultRuleType,
		dealer.defaultRate,
		dealer.defaultAmountKrw,
		dealer.defaultAmountUsd,
		dealer.currency,
	)
	return {
		companyName: dealer.companyName,
		currency: dealer.currency,
		ruleType: r.ruleType,
		value: r.value,
		vatExtraYn: dealer.vatExtraYn,
		shippingExtraYn: dealer.shippingExtraYn,
		dutyExtraYn: dealer.dutyExtraYn,
	}
}

const initRules = (
	rows: DealerPriceSheetRowType[],
	currency: DealerCurrency,
): { [productId: string]: RuleForm } =>
	Object.fromEntries(
		rows.map(aRow => {
			const r = aRow.inherited
				? { ruleType: 'inherit' as RuleSelect, value: '' }
				: ruleToForm(aRow.ruleType, aRow.rate, aRow.amountKrw, aRow.amountUsd, currency)
			return [aRow.productId, { ...r, useYn: aRow.useYn }]
		}),
	)

const style = css({
	...boShared,
	width: '100%',
	display: 'flex',
	flexDirection: 'column',
	gap: 20,
	fontFamily: boFont.ui,

	'.profileBand': {
		display: 'flex',
		flexDirection: 'column',
		gap: 14,
		paddingBottom: 20,
		borderBottom: `1px solid ${mono.line}`,

		'.profileGrid': {
			display: 'grid',
			gridTemplateColumns: 'minmax(180px, 1.4fr) 110px 170px 170px',
			gap: 12,
			'@media (max-width: 1200px)': { gridTemplateColumns: '1fr 1fr' },
		},
		'.optionRow': {
			display: 'flex',
			flexWrap: 'wrap',
			gap: '8px 28px',

			'.optionItem': {
				display: 'flex',
				alignItems: 'center',
				gap: 6,
				cursor: 'pointer',
				'.optionLabel': { fontSize: 13, fontWeight: 500, color: mono.ink },
				'.optionHint': { fontSize: 12, color: mono.faint },
			},
		},
	},

	'.sheetTableContainer': {
		border: `1px solid ${mono.line}`,
		background: mono.surface,

		'.headerCell': {
			fontSize: 11,
			letterSpacing: '.12em',
			textTransform: 'uppercase',
			color: mono.faint,
			fontWeight: 500,
			background: mono.surface,
			borderBottom: `1px solid ${mono.line}`,
			whiteSpace: 'nowrap',
			'&.right': { textAlign: 'right' },
			'&.center': { textAlign: 'center' },
		},
		'.sheetRow': {
			'& td': {
				borderBottom: `1px solid ${mono.lineSoft}`,
				fontSize: 13,
				color: mono.ink,
			},
			'&.off td': { color: mono.faint },
			'.productName': { display: 'block', fontWeight: 500 },
			'.productCode': {
				display: 'block',
				fontSize: 11,
				color: mono.faint,
				letterSpacing: '.08em',
			},
			'.right': { textAlign: 'right' },
			'.center': { textAlign: 'center' },
			'.num': { fontVariantNumeric: 'tabular-nums' },
			'.supply': { fontWeight: 500 },
			'.inherit': { color: mono.faint },
			'.warn': { color: mono.error },
			'&.off .supply': { fontWeight: 400 },
			'& td.error': { color: mono.error },
			'.MuiTextField-root': { minWidth: 120 },
		},
	},

	'.sheetNote': { fontSize: 12, color: mono.faint, margin: 0, lineHeight: 1.7 },

	'.actionRow': { display: 'flex', justifyContent: 'flex-end' },
	// 폰 — 표가 아니라 카드 (2026-09-29 사용자 요청). 컨테이너의 자기 스크롤도 없앤다
	[boMedia.phone]: {
		'.sheetTableContainer': { overflow: 'visible', maxWidth: '100%' },
		'.MuiTable-root': boTableAsCards,
		'.MuiTable-root tbody td .MuiTextField-root, .MuiTable-root tbody td .MuiInputBase-root':
			{ minWidth: 0, width: '100%' },
		'.MuiTable-root tbody td.cardTitle': { fontSize: 15, fontWeight: 500 },
	},
})
